import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { SystemUpdate, Refresh, Close } from '@mui/icons-material';

interface UpdateManagerProps {
  onUpdateAvailable?: () => void;
  onUpdateInstalled?: () => void;
}

const UpdateManager: React.FC<UpdateManagerProps> = ({
  onUpdateAvailable,
  onUpdateInstalled
}) => {

  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showUpdateSnackbar, setShowUpdateSnackbar] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string>('');

  // Check version from server
  const checkVersion = async () => {
    try {
      const response = await fetch('/version.json?t=' + Date.now(), {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const data = await response.json();
      const serverVersion = data.buildTime;

      const localVersion = localStorage.getItem('appVersion');

      if (localVersion && localVersion !== serverVersion) {
        console.log('New version detected:', serverVersion, 'Current:', localVersion);
        // Clear all caches and reload
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }
        localStorage.setItem('appVersion', serverVersion);
        window.location.reload();
        return true;
      } else if (!localVersion) {
        localStorage.setItem('appVersion', serverVersion);
      }

      setCurrentVersion(serverVersion);
      return false;
    } catch (error) {
      console.log('Version check failed:', error);
      return false;
    }
  };

  useEffect(() => {
    // Check version immediately
    checkVersion();

    // Check for updates when component mounts
    checkForUpdates();

    // Set up service worker update listeners
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // New service worker has taken control
        setIsUpdating(false);
        setShowUpdateSnackbar(true);
        onUpdateInstalled?.();
      });

      navigator.serviceWorker.ready.then((registration) => {
        // Listen for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version is available
                setWaitingWorker(newWorker);
                setShowUpdateDialog(true);
                onUpdateAvailable?.();
              }
            });
          }
        });
      });
    }

    // Check for version updates every 2 minutes
    const versionInterval = setInterval(checkVersion, 2 * 60 * 1000);

    // Check for SW updates every 30 minutes
    const swInterval = setInterval(checkForUpdates, 30 * 60 * 1000);

    return () => {
      clearInterval(versionInterval);
      clearInterval(swInterval);
    };
  }, [onUpdateAvailable, onUpdateInstalled]);

  const checkForUpdates = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
      } catch (error) {
        console.log('Update check failed:', error);
      }
    }
  };

  const handleUpdateNow = () => {
    if (waitingWorker) {
      setIsUpdating(true);
      setShowUpdateDialog(false);

      // Tell the waiting service worker to skip waiting and become active
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });

      // Reload the page after a short delay to ensure the new SW is active
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  const handleUpdateLater = () => {
    setShowUpdateDialog(false);
  };

  return (
    <>
      {/* Update Available Dialog */}
      <Dialog
        open={showUpdateDialog}
        onClose={handleUpdateLater}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <SystemUpdate />
          Cập Nhật Ứng Dụng
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'primary.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1
            }}>
              <SystemUpdate sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" align="center" gutterBottom>
              Phiên bản mới đã sẵn sàng!
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary">
              Ứng dụng đã được cập nhật với các tính năng và cải tiến mới.
              Bạn có muốn cập nhật ngay bây giờ không?
            </Typography>
            <Box sx={{
              bgcolor: 'info.light',
              p: 2,
              borderRadius: 2,
              width: '100%',
              border: '1px solid',
              borderColor: 'info.main'
            }}>
              <Typography variant="body2" color="info.dark" align="center">
                💡 Cập nhật sẽ mang lại trải nghiệm tốt hơn và sửa các lỗi đã biết
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleUpdateLater}
            startIcon={<Close />}
            sx={{ minWidth: 120 }}
          >
            Để sau
          </Button>
          <Button
            onClick={handleUpdateNow}
            variant="contained"
            startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
            disabled={isUpdating}
            sx={{ minWidth: 120 }}
          >
            {isUpdating ? 'Đang cập nhật...' : 'Cập nhật ngay'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Success Snackbar */}
      <Snackbar
        open={showUpdateSnackbar}
        autoHideDuration={5000}
        onClose={() => setShowUpdateSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowUpdateSnackbar(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
          icon={<SystemUpdate />}
        >
          Ứng dụng đã được cập nhật thành công! 🎉
        </Alert>
      </Snackbar>

      {/* Loading Overlay during update */}
      {isUpdating && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            color: 'white'
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2, color: 'white' }} />
          <Typography variant="h6" gutterBottom>
            Đang cập nhật ứng dụng...
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.7)">
            Vui lòng đợi trong giây lát
          </Typography>
        </Box>
      )}
    </>
  );
};

export default UpdateManager;
